/**
 * Voice utilities: mic capture -> STT, and TTS playback of replies in Pastor
 * Rob's voice.
 *
 * TTS strategy: the synthesizeSpeech endpoint is a plain request/response
 * call, so long answers are split into sentence-boundary chunks and played
 * as a queue. The first chunk is small to get audio out fast; later chunks
 * synthesize while earlier ones play, which feels like streaming without
 * needing a websocket.
 */

import { authedFetch, errorFromResponse } from './api';

// This file imports only ./api, on purpose: the iOS and Android app runs a copy of it
// (mobile/src/shared, scripts/check-mobile-shared.js), and the $0 checks load it in plain
// node with ./api stood in for. What the web adds (the counts, the team's pick, the browser's
// team mark, who is signed in) comes in through SpeechPlayer's options.

/**
 * The version of prepareSpeechText below, sent with every chunk (stv). The server keeps a copy
 * of it (functions/speechText.js) to check each chunk against the answer it came from, and a $0
 * test keeps the two identical (scripts/speech-text-test.js). Bump both together whenever what
 * it says changes.
 */
export const SPEECH_TEXT_VERSION = 1;

// ---------------------------------------------------------------------------
// What one request for Rob's voice carries (synthesizeSpeech; the voice A/B
// contract's §1.1), as plain functions: scripts/check-voice-client.js runs them.
//
//   text   the chunk to speak (prepareSpeechText's words, as always)
//   crisis the chunk belongs to a crisis reply (its turn had a safety event)
//   rid    the answer's request id; raw, the answer text the stream's grant covers; g, that
//          grant. All three or none: Rob's voice reads only what AI Rob answered, and the
//          server checks each chunk against the grant (the speech guard). The browser never
//          checks a grant; it sends back the latest one the stream gave it.
//   seq    the chunk's place in its reading (0 is the first); src, why the reading started
//   av     this browser has heard Rob's voice before; int, it is marked as the team's
//   voice  a team member's pick, sent only when getMyRole said owner, admin or officer
//   stv    SPEECH_TEXT_VERSION
// A value off its rule is left out, never sent half right.
// ---------------------------------------------------------------------------

export const RID_RULE = /^[A-Za-z0-9_-]{6,32}$/;
export const GRANT_RULE = /^[A-Za-z0-9_-]{43}$/;
export const MAX_RAW = 24000;
const MAX_SEQ = 999;
const READING_SOURCES = ['auto', 'optin', 'listen'];
const TEAM_VOICES = ['grok', 'elevenlabs'];

/** A grant as the stream sent it, or null: { raw, g } with g in shape. */
export function validGrant(raw, g) {
  return typeof raw === 'string' && typeof g === 'string' && GRANT_RULE.test(g) ? { raw, g } : null;
}

/**
 * The cache key for one synthesized chunk: who reads it (the team's pick, or "auto": the server
 * decides), whether it is a crisis chunk, the answer it belongs to (its request id, when it has
 * one) and its words. Replays of an answer are free; a different pick is a different recording,
 * and so is the same sentence in another answer: the server picks the voice once per answer, so a
 * recording from one never plays in the middle of another (a Grok one inside an ElevenLabs answer).
 */
export function speechCacheKey(voice, crisis, text, rid = null) {
  const answer = typeof rid === 'string' && RID_RULE.test(rid) ? `${rid}|` : '';
  return `${voice || 'auto'}|${crisis ? 1 : 0}|${answer}${text}`;
}

/** The JSON body for one chunk. */
export function buildSpeechBody({ text, crisis = false, rid = null, grant = null, seq = null, src = null, av = false, int = false, voice = null, stv = null } = {}) {
  const body = { text };
  if (crisis) body.crisis = true;
  if (typeof rid === 'string' && RID_RULE.test(rid) && grant && typeof grant.raw === 'string'
    && grant.raw.length <= MAX_RAW && typeof grant.g === 'string' && GRANT_RULE.test(grant.g)) {
    body.rid = rid;
    body.raw = grant.raw;
    body.g = grant.g;
  }
  if (Number.isInteger(seq) && seq >= 0 && seq <= MAX_SEQ) body.seq = seq;
  if (READING_SOURCES.includes(src)) body.src = src;
  if (av === true) body.av = true;
  if (int === true) body.int = true;
  if (TEAM_VOICES.includes(voice)) body.voice = voice;
  if (Number.isInteger(stv)) body.stv = stv;
  return body;
}

/**
 * The wait from a reading's request to its first sound, as the counts keep it (utils/
 * analyticsCore.js VOICE_WAIT): under 1, 2, 4 or 8 seconds, else 8 or more.
 */
export function voiceWait(ms) {
  const v = Number(ms);
  if (!(v >= 0)) return 'gte8s';
  if (v < 1000) return 'lt1s';
  if (v < 2000) return 'lt2s';
  if (v < 4000) return 'lt4s';
  if (v < 8000) return 'lt8s';
  return 'gte8s';
}

// Speech-quality opus is ~32kbps; also keeps Safari's AAC recordings small.
const RECORDING_BITS_PER_SECOND = 32000;
// Server rejects base64 above 800k chars; that's 600k raw bytes.
const MAX_RECORDING_BYTES = 600000;

// The numbers someone may need to dial, said the way they are dialed:
// "nine-eight-eight", never "nine hundred eighty-eight".
const DIGIT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const sayDigits = (digits) => digits.split('').map((d) => DIGIT_WORDS[d]).join('-');

// Web addresses the way a person says them out loud.
const SPOKEN_SITES = {
  'findhelp.org': 'find help dot org',
  'thenet.org': 'the net dot org',
  '988lifeline.org': 'nine-eight-eight lifeline dot org',
};
// One character of a web address. One level of parentheses belongs to it
// (…/wiki/Romans_(book)); a bracket, quote or asterisk around it does not.
const URL_CHAR = String.raw`(?:[^\s()<>[\]{}"“”'‘’*]|\([^\s()<>]*\))`;
// With https:// or www. it is an address whatever it ends in (youtu.be,
// .io); without, only on the endings people say aloud ("findhelp.org").
const ADDRESS = new RegExp(String.raw`\b(?:https?:\/\/|www\.)${URL_CHAR}+|\b[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:org|com|net|gov|edu|church)\b(?:\/${URL_CHAR}*)?`, 'gi');
function sayAddress(address) {
  const bare = address.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[?#].*$/, '');
  const [host = '', ...path] = bare.split('/').filter(Boolean);
  const name = host.replace(/:\d+$/, '');
  const site = SPOKEN_SITES[name] || name.split('.').filter(Boolean).join(' dot ');
  // The host, never the rest of a URL. Our own short addresses are the
  // exception, said whole because they are worth typing: "the net dot org
  // slash contact".
  return SPOKEN_SITES[name] && path.length === 1 && /^[a-z-]{1,20}$/.test(path[0]) ? `${site} slash ${path[0]}` : site;
}
// The punctuation that ends the sentence is not part of the address. A bare
// one with a capital in it is two sentences missing a space ("…you
// there.Church is family"), unless it is one of ours ("FindHelp.org").
function sayAddressInSentence(match) {
  const host = match.split('/')[0].replace(/[.,;:!?]+$/, '');
  if (!/^(?:https?:\/\/|www\.)/i.test(match) && /[A-Z]/.test(host) && !SPOKEN_SITES[host.toLowerCase()]) return match;
  const end = match.match(/[.,;:!?]+$/);
  return end ? sayAddress(match.slice(0, -end[0].length)) + end[0] : sayAddress(match);
}

// xAI's speech tags (docs.x.ai, text to speech). The model's words never
// steer the voice: a "[laugh]" or "<whisper>" in an answer is dropped, not
// performed and not read out. A tag we choose to add goes in after this.
const INLINE_TAGS = ['pause', 'long-pause', 'laugh', 'chuckle', 'giggle', 'cry', 'sigh', 'breath', 'inhale', 'exhale', 'tsk', 'tongue-click', 'lip-smack', 'hum-tune'];
const WRAPPING_TAGS = ['soft', 'loud', 'slow', 'fast', 'higher-pitch', 'lower-pitch', 'whisper', 'singing', 'sing-song', 'emphasis', 'build-intensity', 'decrease-intensity'];
const SPEECH_TAGS = new RegExp(String.raw`\[\s*(?:${INLINE_TAGS.join('|')})\s*\]|<\s*\/?\s*(?:${WRAPPING_TAGS.join('|')})\s*>`, 'gi');

/**
 * Strips markdown/formatting so the TTS reads prose, not syntax. The citation
 * footnotes and the source footer exist only in the rendered page, never in
 * this text, so they are never spoken; citations read as their titles.
 */
export function prepareSpeechText(markdown) {
  if (!markdown || typeof markdown !== 'string') return '';
  let text = markdown;

  text = text.replace(/```[\s\S]*?```/g, ' ');            // code blocks
  text = text.replace(/`([^`]*)`/g, '$1');
  text = text.replace(SPEECH_TAGS, (tag) => (/^<\s*\//.test(tag) ? '' : ' '));
  // Angle brackets that are markup go; a "<" that means "less than" stays.
  text = text.replace(/<((?:https?:\/\/|www\.)[^<>\s]+)>/gi, '$1'); // <https://…> autolinks
  text = text.replace(/<\/?[a-z][a-z0-9-]*(?:\s[^<>\n]*)?\/?>/gi, ' '); // <br>, <b>…</b>
  text = text.replace(/<\/?3+(?!\d)/g, '');                 // a heart, "<3" (not "<30")
  // A link's target is never spoken: "[The Net](https://…)" is "The Net",
  // "…/Romans_(book))" and a "title" included.
  text = text.replace(/\]\(\s*(?:https?:\/\/|\/)(?:[^\s()]|\([^\s()]*\))*(?:\s+(?:"[^"\n]*"|'[^'\n]*'))?\s*\)/g, ']');
  text = text.replace(/\[\^?\d{1,3}\]|[¹²³⁰⁴-⁹]+|【[^】]*】/g, ''); // footnote markers
  // Sermon citations "**[Title | 24:21]**" read as just the title, so the
  // sentence still flows ("…jump to Grace, that's where…").
  text = text.replace(/\*\*\s*\[?\s*([^|\]\n*]+?)\s*\|\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*-\s*\d{1,2}:\d{2}(?::\d{2})?)?\s*\]?\s*\*\*/g, '$1');
  text = text.replace(/\[\s*([^|\]\n]+?)\s*\|\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*-\s*\d{1,2}:\d{2}(?::\d{2})?)?\s*\]/g, '$1');
  text = text.replace(/\[timestamp:[^\]]*\]/g, '');
  text = text.replace(/^#{1,6}\s+/gm, '');                 // headers
  text = text.replace(/\[([^\]]*)\]\((?:[^()\n]|\([^()\n]*\))*\)/g, '$1'); // links -> label
  text = text.replace(/\*\*([^*]*)\*\*/g, '$1');           // bold
  text = text.replace(/(^|[^\w*])\*([^\s*][^*]*?)\*(?=[^\w*]|$)/g, '$1$2'); // italics
  text = text.replace(/^[-*+]\s+/gm, '');                  // list bullets
  text = text.replace(/^\d+\.\s+/gm, '');                  // numbered lists

  // Video timestamps — ONLY delimited forms: "(43:08)", "[12:00-14:30]",
  // "(at 43:08)". Bare chapter:verse like "John 3:16" must survive: this is
  // a sermon app and scripture references are the content.
  text = text.replace(/[[(]\s*(?:at\s+)?\d{1,2}:\d{2}(?::\d{2})?(?:\s*[-–]\s*\d{1,2}:\d{2}(?::\d{2})?)?\s*[\])]/g, '');

  text = text.replace(ADDRESS, sayAddressInSentence);
  // HOME is the word to text, however it is typed: "Text Home to 741741".
  text = text.replace(/\b(text)\s+HOME\b/gi, (m, verb, offset, all) => (
    /HOME/.test(m) || /^\s+to\s+741-?741\b/i.test(all.slice(offset + m.length)) ? `${verb} the word home` : m
  ));
  text = text.replace(/\b1-800-(\d{3})-(\d{4})\b/g, (m, a, b) => `one, eight hundred, ${sayDigits(a)}, ${sayDigits(b)}`);
  text = text.replace(/\b741-?741\b/g, `${sayDigits('741')}, ${sayDigits('741')}`);
  text = text.replace(/\b(?:988|911)\b/g, sayDigits);
  text = text.replace(/\bZIP\b/g, 'zip');
  text = text.replace(/\s&\s/g, ' and ');                  // "Suicide & Crisis Lifeline"

  // Where the server cut a long run of the book's own words (functions/bookGuard.js): silence.
  text = text.replace(/[ \t]*\[(?:…|\.\.\.)\]/g, '');
  // "Genius · Chapter Ten" reads "Genius, Chapter Ten": the dot is never said.
  text = text.replace(/[ \t]*[·•][ \t]*/g, ', ');
  text = text.replace(/[|>#_~]/g, ' ');                    // leftover syntax
  text = text.replace(/[[\]]/g, '');                        // "[Blog: Title]" reads as its words
  text = text.replace(/\*/g, ' ');                          // stray asterisks (split-chunk markdown)

  // A line that ends without punctuation (a list item, a heading) still ends
  // a thought: give it a period so the voice pauses instead of running the
  // lines together. A blank line stays a paragraph break, which xAI's voice
  // also pauses on.
  const paragraphs = text.split(/\n\s*\n/);
  return paragraphs
    .map((paragraph, p) => {
      const lines = paragraph.split('\n').map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
      return lines
        .map((line, i) => {
          const last = p === paragraphs.length - 1 && i === lines.length - 1;
          return last || /[.!?:;,…—–-]["'”’)]*$/.test(line) ? line : `${line}.`;
        })
        .join(' ');
    })
    .filter(Boolean)
    .join('\n\n');
}

// ---- The chunker: how a reading is cut into the pieces sent for synthesis -----------------
// functions/speechChunks.js copies everything from the line above to "the chunker ends here", line
// for line (scripts/speech-text-test.js fails when the two differ): the speech guard
// (functions/speech.js) works out every chunk an answer is cut into, the way this code cuts it, and
// Rob's voice reads only those, exactly. Change both together, and SPEECH_TEXT_VERSION with them.

// Per-request server cap is 3000 (SPEECH_MAX_CHARS); stay under it and keep
// the opening chunk short so playback starts quickly.
const FIRST_CHUNK_CHARS = 450;
const CHUNK_CHARS = 1600;

/** Splits text into speakable chunks on sentence boundaries. */
export function splitSpeechChunks(text) {
  const chunks = [];
  const rawSentences = text.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) || [text];

  // A "sentence" with no punctuation for thousands of chars would blow the
  // server's per-request cap — hard-split any oversized one on word bounds.
  const sentences = rawSentences.flatMap((s) => {
    if (s.length <= CHUNK_CHARS) return [s];
    const parts = [];
    let rest = s;
    while (rest.length > CHUNK_CHARS) {
      let cut = rest.lastIndexOf(' ', CHUNK_CHARS);
      if (cut < CHUNK_CHARS / 2) cut = CHUNK_CHARS; // no usable space
      parts.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    if (rest) parts.push(rest);
    return parts;
  });

  let current = '';
  for (const sentence of sentences) {
    const limit = chunks.length === 0 ? FIRST_CHUNK_CHARS : CHUNK_CHARS;
    if (current && (current.length + sentence.length) > limit) {
      chunks.push(current.trim());
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks.filter(Boolean);
}

// Streaming chunk-size ramp. The constraint is physical: chunk N's AUDIO
// must outlast chunk N+1's SYNTHESIS, or the voice goes silent between
// them. Measured on the production voice (2026-07-30): synthesis ~=
// 1.4s + 13.7ms/char; audio ~= 58ms/char. A tiny first chunk starts speech
// fast but only buys ~6s of cover, so sizes ramp: each step's audio covers
// the next step's synthesis with margin (5.8s>4.2s, 14.5s>9.9s, 38s>20.6s).
const STREAM_RAMP = [100, 250, 700];
const STREAM_MAX_CHARS = 1400;

// A chunk worth asking for has a letter or a digit in it. A citation's closing "]**." left on its
// own is just "." once prepared: asking for it would buy a moment of silence.
const SAYABLE = /[\p{L}\p{N}]/u;

// A "[", "(" or "**" still open this many characters on, or across a line break, was never going
// to close (a citation missing its "]", an answer cut off, a ":("): it stops holding cuts back.
const SPAN_LIMIT = 200;

/**
 * Where the stream may cut `text` (a prefix of what is not yet spoken), outside every [ ], ( )
 * and ** ** it has opened: `sentence`, just after its last sentence end (the punctuation, any
 * closing quote, bracket or asterisk, and the whitespace after it), and `space`, just after its
 * last whitespace; 0 for none. A cut inside a sermon citation ("**[What If God Is Real? |
 * 12:04]**", "**What If God Is Real? | 12:04**", "**[Mr. Rogers | 8:15]**") or a link would read
 * its time or address aloud ("twelve oh four"). A span still open after SPAN_LIMIT characters or a
 * line break counts as closed from there, so one stray "(" never holds back the rest of an answer.
 */
export function cutPoints(text) {
  let square = 0;
  let round = 0;
  let bold = false;
  let openedAt = -1; // where the span still open began
  let sentence = 0;
  let space = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (openedAt >= 0 && (ch === '\n' || i - openedAt > SPAN_LIMIT)) {
      square = 0;
      round = 0;
      bold = false;
      openedAt = -1;
    }
    const wasOpen = openedAt >= 0;
    if (ch === '*' && text[i + 1] === '*') {
      bold = !bold;
      i += 1;
    } else if (ch === '[') square += 1;
    else if (ch === ']') { if (square) square -= 1; }
    else if (ch === '(') round += 1;
    else if (ch === ')') { if (round) round -= 1; }
    else if (!wasOpen && /\s/.test(ch)) {
      space = i + 1;
      let j = i - 1;
      while (j >= 0 && `"')]*`.includes(text[j])) j -= 1;
      if (j >= 0 && '.!?'.includes(text[j])) sentence = i + 1;
    }
    const open = square > 0 || round > 0 || bold;
    if (open && !wasOpen) openedAt = i;
    else if (!open) openedAt = -1;
  }
  return { sentence, space };
}

/**
 * Where to cut a window that has no sentence end: its last whitespace outside every span, when
 * that is past the middle; else its last whitespace at all (a stray "(" holds nothing back here),
 * when that is; else the nearer of the two; a window with no whitespace at all is cut at its end.
 * Never in the middle of a word that has a space to its left: "nine-eight-eight" stays whole.
 */
function wordCut(window, cuts, threshold) {
  const anySpace = Math.max(window.lastIndexOf(' '), window.lastIndexOf('\n')) + 1;
  if (cuts.space > threshold / 2) return cuts.space;
  if (anySpace > threshold / 2) return anySpace;
  return cuts.space || anySpace || window.length;
}

/**
 * Cuts what a stream has not yet spoken (`rest`, raw markdown) into the chunks it can send now, as
 * prepared text: { pieces, rest, count }. `count` is how many chunks the reading has made so far
 * (the ramp's step); `flush`: the stream has ended, so everything is cut. Where it cuts depends
 * only on the text, never on how the stream happened to arrive, so a replay makes the same chunks
 * (the cache hits) and so does the server's copy (the speech guard).
 */
export function cutSpeechText(rest, count = 0, flush = false) {
  const pieces = [];
  let raw = typeof rest === 'string' ? rest : '';
  let made = count;
  while (raw.length) {
    const threshold = STREAM_RAMP[made] ?? STREAM_MAX_CHARS;
    if (raw.length < threshold && !flush) break;
    let cut;
    if (flush && raw.length <= threshold) {
      cut = raw.length;
    } else {
      // The last sentence end within a FIXED window of `threshold` characters, never inside a
      // citation or a link (cutPoints).
      const window = raw.slice(0, threshold);
      const cuts = cutPoints(window);
      if (cuts.sentence) cut = cuts.sentence;
      // The end of the stream, or a run-on with no sentence end in reach: a word bound.
      else if (flush || raw.length >= threshold * 3) cut = wordCut(window, cuts, threshold);
      else break; // wait for a sentence end, or the end of the stream
    }
    const segment = raw.slice(0, cut);
    raw = raw.slice(cut);
    const prepared = prepareSpeechText(segment);
    // Server cap safety: split any monster segment. A piece with nothing to say is not sent.
    for (const piece of prepared ? splitSpeechChunks(prepared) : []) {
      if (!SAYABLE.test(piece)) continue;
      pieces.push(piece);
      made += 1;
    }
  }
  return { pieces, rest: raw, count: made };
}

/**
 * Every chunk a reading of `text` (an answer, or the part of it streamed so far) is cut into:
 * what the stream cuts as it goes and, when `ended`, the rest, as the stream's end cuts it.
 */
export function speechChunksOf(text, { ended = false } = {}) {
  const streamed = cutSpeechText(typeof text === 'string' ? text : '', 0, false);
  if (!ended) return streamed.pieces;
  return [...streamed.pieces, ...cutSpeechText(streamed.rest, streamed.count, true).pieces];
}
// ---- the chunker ends here ---------------------------------------------------------------

// Tiny valid silent WAV, used to unlock the audio element inside a gesture.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

// "1" once this browser has played Rob's voice: sent with each request (av) and on the counts'
// batches (utils/analytics.js), so only people new to his voice join the voice test. Nothing
// is kept where there is no localStorage (the phone app).
const HEARD_KEY = 'apr-av';

function heardBefore() {
  try { return localStorage.getItem(HEARD_KEY) === '1'; } catch (e) { return false; }
}

function markHeard() {
  try { localStorage.setItem(HEARD_KEY, '1'); } catch (e) { /* storage blocked, or none */ }
}

function now() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

/** A response header the server exposes to the team only (X-Voice, X-Voice-Fallback), or null. */
function header(response, name) {
  try { return (response.headers && response.headers.get(name)) || null; } catch (e) { return null; }
}

/**
 * Plays replies aloud.
 *
 * Streaming: beginStream() returns a session whose feed() accepts text
 * deltas as they arrive from the SSE stream. Speech starts after the first
 * complete early sentence — the reply is being spoken while the rest is
 * still streaming in.
 *
 * Gapless: two alternating <audio> elements; the next chunk's audio is
 * preloaded into the idle element while the current one plays, so chunk
 * seams don't sound like the voice "refiring".
 *
 * Concurrency: every beginStream()/stop() bumps a generation counter and
 * all continuations of stale generations no-op — overlapping plays can
 * never double-bill or become unstoppable.
 *
 * iOS/Safari: audio may only start inside a user gesture. unlock() must be
 * called synchronously from a click/submit handler; it primes both shared
 * elements.
 *
 * Cost: synthesized chunks are cached by who reads them, crisis or not, the answer and
 * their words (speechCacheKey), so replays are free. Someone else signing in
 * drops the cache.
 *
 * Crisis replies (the turn's safety event, which arrives before the first
 * word) say so when they ask for speech: beginStream({ crisis }), or
 * setCrisis() on a session already started. The server decides their pace,
 * which for now is the normal one: slower would reach 988 later
 * (functions/voiceAb.js CRISIS_SPEECH_SPEED, part of Grok's version label; a
 * crisis grant, not this flag, is what the speech guard trusts). No [pause]
 * tag is added. Measured on Rob's voice
 * (2026-09-30), xAI stretches one that ends a chunk into two seconds of
 * silence and ignores one that starts a chunk, and a crisis reply's first
 * chunk is its first sentence alone, with the stream's own gap after it while
 * the next one is made.
 *
 * The speech guard: every chunk goes with the answer's request id and the
 * latest grant the stream sent for it ({ raw, g }: feed(delta, g), end(g)), so
 * the server can tell it reads only what AI Rob answered. A saved answer's
 * grant comes with play(). No grant: none of the three is sent.
 *
 * Readings (the counts): one reading is one answer read aloud, from its first
 * request to whatever ends it; a restart after the stream's `reset` is the same
 * reading (beginStream({ restart })). It reports through onReading: req (why it
 * started), play (how long until the first sound) and, exactly once for a
 * reading that sent req, end: done (the last chunk ended on its own), error,
 * or the `by` given to stop() (stop, type, off, ask, leave), the first cause
 * winning, with the whole seconds of audio actually played.
 *
 * Options, all optional:
 *   onStateChange(state)  'idle' | 'loading' | 'playing' | 'paused'
 *   onError(err)          a reading failed
 *   onReading(moment)     { ph: 'req', src } | { ph: 'play', w } | { ph: 'end', by, sec, first? }
 *                         (first: the reading began before this browser had ever played Rob's
 *                         voice: the one the voice test's "let Rob finish" reads)
 *   onVoice(heard)        the team's view of who read the latest chunk ({ voice, fallback }
 *                         from the response headers, which only a team member's requests get),
 *                         or null when a new reading starts
 *   context()             { uid, int, voice } at the moment a chunk is asked for: who is signed
 *                         in (the cache is theirs), whether this browser is marked as the team's,
 *                         and a team member's pick of voice (null for anyone else)
 *
 * A subclass (the phone app's NativeSpeechPlayer) may replace fetchChunk(text, crisis, fields),
 * playUrl(url, gen), preloadNext(url), haltAudio() and the controls; fetchChunk resolves to a
 * URL its playUrl can play, as it always has.
 */
export class SpeechPlayer {
  constructor({ onStateChange, onError, onReading, onVoice, context } = {}) {
    this.onStateChange = onStateChange || (() => {});
    this.onError = onError || (() => {});
    this.onReading = onReading || (() => {});
    this.onVoice = onVoice || (() => {});
    this.context = context || (() => ({}));
    this.els = [null, null];
    this.elIdx = 0;
    this.gen = 0;
    this.active = false;
    this.playingEl = null;
    this.playing = null; // { el, reading, counted }: whose seconds the playing element adds to
    this.userPaused = false;
    this.reading = null;
    this.cache = new Map(); // speechCacheKey -> Promise<object URL>
    this.heardBy = new Map(); // object URL -> { voice, fallback }, for the team's bar on a replay
    this.cacheUid = undefined;
  }

  ensureEls() {
    for (let i = 0; i < 2; i++) {
      if (!this.els[i]) {
        this.els[i] = new Audio();
        this.els[i].preload = 'auto';
      }
    }
  }

  /** Call synchronously inside a real user gesture (click/submit). */
  unlock() {
    this.ensureEls();
    this.countPlaying(); // the silent clip replaces whatever was playing: keep its seconds
    for (const el of this.els) {
      try {
        el.src = SILENT_WAV;
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
      } catch (e) { /* priming is best-effort */ }
    }
  }

  // ---------------------------------------------------------------- readings

  startReading({ src, rid }) {
    this.reading = {
      src: READING_SOURCES.includes(src) ? src : 'listen',
      rid: typeof rid === 'string' ? rid : null,
      seq: 0,
      asked: false,
      askedAt: 0,
      heard: false,
      sec: 0,
      // This browser had never played Rob's voice when the reading began: its end is the end of
      // the first reading the voice test measures ("let Rob finish his first answer").
      first: !heardBefore(),
    };
    this.onVoice(null);
  }

  /** Ends the reading in progress, if any: its end is counted once, if it asked for anything. */
  endReading(by) {
    const reading = this.reading;
    if (!reading) return;
    this.countPlaying();
    this.reading = null;
    if (reading.asked) this.onReading({ ph: 'end', by, sec: Math.floor(reading.sec), ...(reading.first ? { first: true } : {}) });
  }

  /** The playing element's seconds so far go to the reading it was playing for, once. */
  countPlaying() {
    const p = this.playing;
    if (!p || p.counted) return;
    p.counted = true;
    if (p.reading) p.reading.sec += Number(p.el.currentTime) || 0;
  }

  /** The reading's first audio started. */
  noteHeard(reading) {
    if (!reading || reading.heard) return;
    reading.heard = true;
    markHeard();
    this.onReading({ ph: 'play', w: voiceWait(now() - reading.askedAt) });
  }

  // ----------------------------------------------------------------- streams

  /**
   * Starts a speech session fed incrementally. Returns { feed, end, setCrisis }.
   * All SpeechPlayer playback flows through here — play() is just a
   * beginStream that receives everything at once.
   *
   *   crisis     a crisis reply (or setCrisis() once its safety event arrives)
   *   src, rid   why this reading started ('auto', 'optin', 'listen'), and the answer's request id
   *   grant      { raw, g }: a saved answer's grant (a stream's arrive with feed and end)
   *   restart    the stream started over (`reset`): the same reading carries on from here
   *   supersede  what ends a reading still going when another starts: 'stop' (another
   *              answer's Listen) or 'ask' (a question sent while Rob was speaking)
   */
  beginStream({ crisis = false, src = 'auto', rid = null, grant = null, restart = false, supersede = 'stop' } = {}) {
    const answer = typeof rid === 'string' ? rid : null;
    const continuing = Boolean(restart && this.reading && this.reading.rid === answer);
    if (!continuing) this.endReading(supersede);
    const gen = ++this.gen;
    this.haltAudio();
    if (!continuing) this.startReading({ src, rid: answer });
    this.active = true;
    this.userPaused = false;
    this.onStateChange('loading');

    const state = {
      raw: '', // text not yet cut into chunks
      full: '', // every delta this session was fed: what the stream's grant covers
      grant: grant ? validGrant(grant.raw, grant.g) : null,
      queue: [], // { text, seq }
      done: false,
      chunkCount: 0,
      wake: null,
      crisis,
    };
    // Event-driven pump: feed()/end() wake it immediately instead of a poll
    // timer — the first chunk starts the moment its text exists, and a
    // pause can never race a sleeping pump into starting new audio. The
    // 500ms race is a fallback so a stopped session's parked pump drains.
    const wake = () => {
      if (state.wake) { const w = state.wake; state.wake = null; w(); }
    };

    // Pull as many speakable chunks out of the raw buffer as thresholds
    // allow (cutSpeechText: the cuts depend only on the text, so a replay
    // makes the same chunks and the synthesis cache hits, and the server's
    // copy makes them too, which is what the speech guard checks).
    const extract = (flush) => {
      const cut = cutSpeechText(state.raw, state.chunkCount, flush);
      state.raw = cut.rest;
      state.chunkCount = cut.count;
      for (const piece of cut.pieces) state.queue.push({ text: piece, seq: null });
    };

    const pump = async () => {
      try {
        let finished = false;
        while (gen === this.gen) {
          if (state.queue.length === 0) {
            if (state.done) { finished = true; break; }
            await Promise.race([
              new Promise((r) => { state.wake = r; }),
              new Promise((r) => setTimeout(r, 500)),
            ]);
            continue;
          }
          const item = state.queue.shift();
          // If a gap does open (slow network beats the ramp), say so on the
          // bar rather than sitting on "speaking" through dead air.
          const slowTimer = setTimeout(() => {
            if (gen === this.gen && !this.userPaused) this.onStateChange('loading');
          }, 400);
          let url;
          try {
            url = await this.fetchChunk(item.text, state.crisis, this.chunkFields(item, state));
          } finally {
            clearTimeout(slowTimer);
          }
          if (gen !== this.gen) return;
          // Warm the next chunk: synthesize it and preload into the idle
          // element so the seam between chunks is inaudible.
          const next = state.queue[0];
          if (next) {
            this.fetchChunk(next.text, state.crisis, this.chunkFields(next, state))
              .then((u) => { if (gen === this.gen) this.preloadNext(u); })
              .catch(() => {});
          }
          this.onStateChange('playing');
          await this.playUrl(url, gen);
          if (gen !== this.gen) return;
        }
        // The last chunk ended on its own.
        if (finished && gen === this.gen) this.endReading('done');
      } catch (err) {
        if (gen === this.gen) {
          this.endReading('error');
          this.onError(err);
        }
      } finally {
        if (gen === this.gen) {
          this.active = false;
          this.onStateChange('idle');
        }
      }
    };
    const pumping = pump();
    pumping.catch(() => {});

    return {
      // delta: the chunk's words; g: the grant the stream sent with it, covering every delta so far.
      feed: (delta, g) => {
        if (gen !== this.gen || state.done || !delta) return;
        state.raw += delta;
        state.full += delta;
        const fresh = g ? validGrant(state.full, g) : null;
        if (fresh) state.grant = fresh;
        extract(false);
        if (state.queue.length) wake();
      },
      // g: the done event's grant, covering the whole answer.
      end: (g) => {
        if (gen !== this.gen || state.done) return;
        const fresh = g ? validGrant(state.full, g) : null;
        if (fresh) state.grant = fresh;
        extract(true);
        state.done = true;
        wake();
      },
      // The turn is a crisis turn: its safety event comes before any word.
      setCrisis: () => {
        if (gen === this.gen) state.crisis = true;
      },
    };
  }

  /** Reads a whole answer. A saved answer passes its request id and grant (aiMessage.turn, .grant). */
  play(markdown, { crisis = false, src = 'listen', rid = null, grant = null } = {}) {
    const text = markdown || '';
    const session = this.beginStream({ crisis, src, rid, grant: grant ? { raw: text, g: grant } : null });
    session.feed(text);
    session.end();
  }

  /**
   * What one chunk's request carries besides its words: its place in the reading, why the
   * reading started, the answer's request id and grant, and this browser's facts. The first
   * request of a reading, from the server or the cache, is its `req`.
   */
  chunkFields(item, state) {
    const reading = this.reading;
    if (item.seq === null) item.seq = reading ? reading.seq++ : 0;
    if (reading && !reading.asked) {
      reading.asked = true;
      reading.askedAt = now();
      this.onReading({ ph: 'req', src: reading.src });
    }
    let ctx = {};
    try { ctx = this.context() || {}; } catch (e) { ctx = {}; }
    return {
      rid: reading ? reading.rid : null,
      grant: state.grant,
      seq: item.seq,
      src: reading ? reading.src : null,
      av: heardBefore(),
      int: ctx.int === true,
      voice: ctx.voice || null,
      stv: SPEECH_TEXT_VERSION,
      uid: ctx.uid || null,
      reading,
    };
  }

  /** Someone else signed in: their requests never reuse this person's recordings. */
  forUser(uid) {
    const who = uid || null;
    if (who === this.cacheUid) return;
    if (this.cacheUid !== undefined && this.cache.size) {
      const old = [...this.cache.values()];
      this.cache.clear();
      this.heardBy.clear();
      // Revoked a little later: one of them may still be playing.
      setTimeout(() => old.forEach((p) => p.then((url) => URL.revokeObjectURL(url), () => {})), 60000);
    }
    this.cacheUid = who;
  }

  /**
   * One chunk's audio, as an object URL. The cache maps the chunk's key -> PROMISE of that,
   * set before the request resolves. That makes it double as in-flight deduplication: when the
   * prefetch and the pump ask for the same chunk seconds apart, the second caller awaits the
   * first request instead of paying for a second synthesis — which was audible as a long dead
   * pause after the first sentence (and billed twice).
   */
  fetchChunk(text, crisis = false, fields = {}) {
    this.forUser(fields.uid);
    const reading = fields.reading || null;
    const tell = (url) => {
      const heard = this.heardBy.get(url);
      if (heard && reading && reading === this.reading) this.onVoice(heard);
      return url;
    };
    // The answer is in the key when the chunk goes with its grant (the server keeps one voice per
    // answer then); without one (no grants on the server), the words alone, as before.
    const key = speechCacheKey(fields.voice, crisis, text, fields.grant ? fields.rid : null);
    if (this.cache.has(key)) return this.cache.get(key).then(tell);
    const body = buildSpeechBody({ ...fields, text, crisis });
    const promise = (async () => {
      const response = await authedFetch('synthesizeSpeech', body);
      if (!response.ok) throw await errorFromResponse(response);
      const voice = header(response, 'X-Voice');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (voice) this.heardBy.set(url, { voice, fallback: header(response, 'X-Voice-Fallback') });
      return url;
    })();
    // A failed synthesis must not poison the cache — allow retry.
    promise.catch(() => { if (this.cache.get(key) === promise) this.cache.delete(key); });
    this.cache.set(key, promise);
    // Bound memory: keep the most recent ~40 chunks (a few long replies).
    if (this.cache.size > 40) {
      const oldest = this.cache.keys().next().value;
      const evicted = this.cache.get(oldest);
      this.cache.delete(oldest);
      evicted.then((url) => { this.heardBy.delete(url); URL.revokeObjectURL(url); }, () => {});
    }
    return promise.then(tell);
  }

  /**
   * Buffers the upcoming chunk in the element that plays next.
   *
   * Index note: playUrl() takes els[elIdx] and THEN flips elIdx, so while a
   * chunk is playing, `this.elIdx` already points at the idle element — the
   * one that plays next. (Using elIdx^1 here was a launch bug: it assigned
   * src to the LIVE element, aborting playback mid-sentence and hanging the
   * pump on an `ended` event that never came.)
   */
  preloadNext(url) {
    this.ensureEls();
    const next = this.els[this.elIdx];
    // Never touch an element that is somehow mid-playback.
    if (next.paused && next.src !== url) next.src = url;
  }

  playUrl(url, gen) {
    return new Promise((resolve, reject) => {
      this.ensureEls();
      const audio = this.els[this.elIdx];
      this.elIdx ^= 1; // the other element preloads while this one plays
      this.playingEl = audio;
      const reading = this.reading;
      const playing = { el: audio, reading, counted: false };
      this.playing = playing;
      const done = () => {
        audio.onended = null;
        audio.onerror = null;
        if (this.playing === playing) this.countPlaying();
        resolve();
      };
      audio.onended = done;
      audio.onerror = () => {
        // A stale generation halting the element is not a playback failure.
        if (gen !== this.gen) { done(); return; }
        audio.onended = null;
        audio.onerror = null;
        reject(new Error('Audio playback failed'));
      };
      if (audio.src !== url) audio.src = url;
      audio.play().then(() => {
        if (gen !== this.gen) return;
        this.noteHeard(reading);
        // A pause requested during the between-chunks gap applies now.
        if (this.userPaused) audio.pause();
      }, (err) => {
        if (gen !== this.gen) { done(); return; }
        reject(err);
      });
    });
  }

  haltAudio() {
    this.countPlaying();
    for (const el of this.els) {
      if (!el) continue;
      try {
        el.pause();
        el.removeAttribute('src');
      } catch (e) { /* halting is best-effort */ }
    }
  }

  /**
   * Freezes playback in place; resume() picks up mid-word. The flag is
   * sticky: pausing during a between-chunks gap still holds the NEXT chunk
   * (playUrl re-checks it right after starting).
   */
  pause() {
    if (!this.active) return;
    this.userPaused = true;
    if (this.playingEl && !this.playingEl.paused) this.playingEl.pause();
    this.onStateChange('paused');
  }

  resume() {
    if (!this.active) return;
    this.userPaused = false;
    if (this.playingEl && this.playingEl.paused && this.playingEl.src) {
      const p = this.playingEl.play();
      if (p && p.catch) p.catch(() => {});
    }
    this.onStateChange('playing');
  }

  /**
   * Stops everything. `by` is why, for the reading's end: 'stop' (the Stop button, the Listen
   * toggle, the answer stopped or left), 'type' (Type instead, or the mic), 'off' (Read aloud
   * switched off), 'ask' (a question sent while Rob was speaking), 'leave' (the page closing),
   * 'error' (the answer failed).
   */
  stop(by = 'stop') {
    this.endReading(by);
    this.gen += 1; // every in-flight continuation goes stale
    this.haltAudio();
    this.playingEl = null;
    this.playing = null;
    if (this.active) {
      this.active = false;
      this.onStateChange('idle');
    }
  }

  /** Releases cached audio. Call on unmount. */
  dispose() {
    this.stop();
    for (const p of this.cache.values()) p.then((url) => URL.revokeObjectURL(url), () => {});
    this.cache.clear();
    this.heardBy.clear();
  }
}

/** Picks a MediaRecorder mime type the current browser can produce. */
function pickRecordingMime() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  if (typeof MediaRecorder === 'undefined') return null;
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export function micSupported() {
  return typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';
}

/**
 * Records from the microphone until stop() is called (or maxMs elapses),
 * then resolves the returned promise with { blob, mimeType }. `onStream`
 * receives the live microphone stream once it opens (the level line in the
 * composer reads it; nothing is sent anywhere from it).
 */
export function startRecording({ maxMs = 60000, onStream = null } = {}) {
  const mimeType = pickRecordingMime();
  let recorder = null;
  let timer = null;
  let stopped = false;

  const result = new Promise((resolve, reject) => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (stopped) { // stop() raced the permission prompt
        stream.getTracks().forEach((t) => t.stop());
        reject(new Error('cancelled'));
        return;
      }
      const chunks = [];
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: RECORDING_BITS_PER_SECOND,
      });
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        clearTimeout(timer);
        stream.getTracks().forEach((t) => t.stop());
        resolve({
          blob: new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }),
          mimeType: recorder.mimeType || mimeType || 'audio/webm',
        });
      };
      recorder.onerror = (e) => {
        clearTimeout(timer);
        stream.getTracks().forEach((t) => t.stop());
        reject(e.error || new Error('Recording failed'));
      };
      recorder.start();
      if (onStream) {
        try { onStream(stream); } catch (e) { /* the level line is cosmetic */ }
      }
      timer = setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, maxMs);
    }).catch(reject);
  });

  return {
    result,
    stop() {
      stopped = true;
      if (recorder && recorder.state === 'recording') recorder.stop();
    },
    cancel() {
      stopped = true;
      if (recorder && recorder.state === 'recording') {
        recorder.ondataavailable = null;
        recorder.stop();
      }
    },
  };
}

/** Sends a recording to the transcription endpoint; returns the text. */
export async function transcribe({ blob, mimeType }) {
  if (blob.size > MAX_RECORDING_BYTES) {
    const err = new Error('Recording too large');
    err.safeMessage = 'That recording was too long to transcribe. Try a shorter question.';
    throw err;
  }
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Could not read recording'));
    reader.readAsDataURL(blob);
  });

  const response = await authedFetch('transcribeAudio', { audio: base64, mimeType });
  if (!response.ok) throw await errorFromResponse(response);
  const data = await response.json();
  return (data.text || '').trim();
}
